/**
 * Bonsai Mail Context Provider
 * Manages Microsoft OAuth state and provides Bonsai-specific API helpers
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

import { API } from '../config/api';

const BonsaiContext = createContext(null);

export function useBonsai() {
  const context = useContext(BonsaiContext);
  if (!context) {
    // Graceful fallback for components embedded outside BonsaiProvider
    // (e.g. PipelineTrackerCore on Dashboard). Returns safe defaults.
    return {
      loading: false,
      authenticated: false,
      configured: false,
      user_id: localStorage.getItem("bonsai_user_id") || "",
      user_email: localStorage.getItem("bonsai_user_email") || "",
      user_name: "",
      is_admin: false,
      caseminder_linked: false,
      login: () => {},
      logout: () => {},
      checkAuthStatus: () => {},
      bonsaiApi: null,
    };
  }
  return context;
}

export function BonsaiProvider({ children }) {
  const [authStatus, setAuthStatus] = useState({
    loading: true,
    authenticated: false,
    configured: false,
    user_id: "",
    user_email: "",
    user_name: "",
    is_admin: false,
    caseminder_linked: false,
    auth_error: null
  });

  // Get stored Bonsai user ID from URL params or sessionStorage
  const getBonsaiUserId = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlUserId = urlParams.get("bonsai_user_id");
    const authSuccess = urlParams.get("auth");
    
    
    if (urlUserId) {
      sessionStorage.setItem("bonsai_user_id", urlUserId);
      localStorage.setItem("bonsai_user_id", urlUserId);
      // Also set bonsai_auth so ProtectedRoute (for CaseMinder pages) works
      if (authSuccess === "success") {
        localStorage.setItem("bonsai_auth", "true");
        localStorage.setItem("bonsai_last_activity", Date.now().toString());
        sessionStorage.setItem("bonsai_auth", "true");
        sessionStorage.setItem("bonsai_last_activity", Date.now().toString());
      }
      // Clean URL — guard against Outlook iframe which blocks history.replaceState
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch(e) {
        // Silently ignore — Outlook sandboxed iframes restrict the history API
      }
      return urlUserId;
    }
    
    const storedId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
    return storedId;
  }, []);

  // Check auth status
  // BM-H130 -- Emily reported the Outlook add-in was stuck on "Connecting to
  // Backend..." indefinitely. The previous implementation retried every 4s
  // forever on any network error, so the pane never surfaced a real failure
  // and the user had no way to know whether to wait, reconnect, or escalate.
  // We now cap cold-start retries at BONSAI_AUTH_MAX_RETRIES; after that the
  // pane renders a real error with a manual Retry button.
  const BONSAI_AUTH_MAX_RETRIES = 3;
  const BONSAI_AUTH_RETRY_DELAY_MS = 4000;
  const retryCountRef = useRef(0);

  const checkAuthStatus = useCallback(async () => {
    try {
      const userId = getBonsaiUserId();
      
      const response = await axios.get(`${API}/bonsai/auth/status`, {
        headers: userId ? { "X-Bonsai-User-Id": userId } : {},
        timeout: 15000
      });
      
      retryCountRef.current = 0;
      setAuthStatus({
        loading: false,
        auth_error: null,
        ...response.data
      });
      
      // Store user_id if authenticated
      if (response.data.authenticated && response.data.user_id) {
        sessionStorage.setItem("bonsai_user_id", response.data.user_id);
        localStorage.setItem("bonsai_user_id", response.data.user_id);
      } else {
      }

      // Store email for fee earner filtering
      if (response.data.user_email) {
        localStorage.setItem("bonsai_user_email", response.data.user_email);
        sessionStorage.setItem("bonsai_user_email", response.data.user_email);
        // Also write a "user" object matching the format Dashboard.jsx expects
        const existingUser = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem("user", JSON.stringify({
          ...existingUser,
          user_email: response.data.user_email,
          email: response.data.user_email,
          name: response.data.user_name || existingUser.name || "",
        }));
      }
    } catch (error) {
      console.error("[BonsaiContext] Failed to check Bonsai auth status:", error?.message || error);
      console.error("[BonsaiContext] Error details:", error?.response?.status, error?.response?.data);
      const hadResponse = !!error?.response;
      const attempt = retryCountRef.current + 1;
      const canRetry = !hadResponse && attempt <= BONSAI_AUTH_MAX_RETRIES;
      const humanError = hadResponse
        ? `Backend returned ${error.response.status}. Please retry.`
        : (canRetry
            ? `Backend starting up — retry ${attempt}/${BONSAI_AUTH_MAX_RETRIES}...`
            : `Cannot reach Bonsai backend after ${BONSAI_AUTH_MAX_RETRIES} attempts. Check your network or contact support.`);
      setAuthStatus(prev => ({
        ...prev,
        loading: false,
        auth_error: humanError,
      }));
      if (canRetry) {
        retryCountRef.current = attempt;
        setTimeout(() => {
          console.log(`[BonsaiContext] Retrying auth status check (${attempt}/${BONSAI_AUTH_MAX_RETRIES})...`);
          checkAuthStatus();
        }, BONSAI_AUTH_RETRY_DELAY_MS);
      } else if (!hadResponse) {
        // Give up: user must hit Retry now. Reset counter so the manual retry
        // gets a fresh round of automatic attempts.
        retryCountRef.current = 0;
      }
    }
  }, [getBonsaiUserId]);

  // Initial auth check
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Login - redirect to MS OAuth
  const login = async (returnUrl = "/dashboard") => {
    try {
      const response = await axios.get(`${API}/bonsai/auth/login`, {
        params: { return_url: returnUrl }
      });
      
      
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      console.error("[BonsaiContext] Failed to initiate login:", error);
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      const userId = getUserId();
      await axios.post(`${API}/bonsai/auth/logout`, {}, {
        headers: userId ? { "X-Bonsai-User-Id": userId } : {}
      });
    } catch (error) {
      console.error("Failed to logout:", error);
    } finally {
      sessionStorage.removeItem("bonsai_user_id");
      localStorage.removeItem("bonsai_user_id");
      localStorage.removeItem("bonsai_auth");
      localStorage.removeItem("bonsai_last_activity");
      sessionStorage.removeItem("bonsai_auth");
      sessionStorage.removeItem("bonsai_last_activity");
      setAuthStatus({
        loading: false,
        authenticated: false,
        configured: authStatus.configured,
        user_id: "",
        user_email: "",
        user_name: "",
        is_admin: false,
        caseminder_linked: false
      });
    }
  };

  // API helper with auth headers
  const getUserId = () => sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  const bonsaiApi = {
    get: (endpoint, config = {}) => {
      const userId = getUserId();
      return axios.get(`${API}/bonsai${endpoint}`, {
        ...config,
        headers: {
          ...config.headers,
          ...(userId ? { "X-Bonsai-User-Id": userId } : {})
        }
      });
    },
    post: (endpoint, data, config = {}) => {
      const userId = getUserId();
      return axios.post(`${API}/bonsai${endpoint}`, data, {
        ...config,
        headers: {
          ...config.headers,
          ...(userId ? { "X-Bonsai-User-Id": userId } : {})
        }
      });
    },
    put: (endpoint, data, config = {}) => {
      const userId = getUserId();
      return axios.put(`${API}/bonsai${endpoint}`, data, {
        ...config,
        headers: {
          ...config.headers,
          ...(userId ? { "X-Bonsai-User-Id": userId } : {})
        }
      });
    },
    delete: (endpoint, config = {}) => {
      const userId = getUserId();
      return axios.delete(`${API}/bonsai${endpoint}`, {
        ...config,
        headers: {
          ...config.headers,
          ...(userId ? { "X-Bonsai-User-Id": userId } : {})
        }
      });
    }
  };

  const value = {
    ...authStatus,
    login,
    logout,
    checkAuthStatus,
    bonsaiApi
  };

  return (
    <BonsaiContext.Provider value={value}>
      {children}
    </BonsaiContext.Provider>
  );
}

export default BonsaiContext;
